import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Newspaper, Pin, Star, Search as SearchIcon, Eye, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import api from "../lib/api";

const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

export default function NewsPage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("");
  const [q, setQ] = useState("");

  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/news?limit=200");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e?.response?.status ? `HTTP ${e.response.status}` : "network");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNews();
    api.get("/news-categories").then((r) => setCats(r.data)).catch(() => {});
  }, [fetchNews]);

  const filtered = useMemo(() => {
    return items.filter((n) => {
      if (activeCat && n.category !== activeCat) return false;
      if (q) {
        const s = q.toLowerCase();
        return (n.title || "").toLowerCase().includes(s)
            || (n.excerpt || "").toLowerCase().includes(s)
            || (n.category || "").toLowerCase().includes(s);
      }
      return true;
    });
  }, [items, activeCat, q]);

  return (
    <div data-testid="news-page">
      {/* Bandeau haut : premium éditorial */}
      <section className="relative bg-ink text-white overflow-hidden grain">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(212,175,55,0.15),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_70%,rgba(200,16,46,0.12),transparent_55%)]" />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
          <div className="flex items-center justify-between gap-8 flex-wrap">
            <div className="animate-fade-up">
              <div className="eyebrow eyebrow-on-dark flex items-center gap-3">
                <span className="w-8 h-px bg-gold" />
                <Newspaper className="w-3.5 h-3.5" /> Actualités & Communiqués
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.05] tracking-tight">
                Toute l'actualité <span className="text-gold italic font-light">du Front Uni.</span>
              </h1>
              <p className="mt-3 text-white/65 text-sm max-w-xl">
                Plaidoyer européen, action humanitaire, justice : retrouvez chaque jour les engagements de la diaspora congolaise.
              </p>
            </div>

            <div className="hidden lg:flex items-center gap-4 text-xs text-gold border border-gold/25 rounded-sm px-4 py-2">
              <span className="live-dot" />
              <span className="uppercase tracking-[0.22em] font-semibold">
                {loading ? "Chargement…" : error ? "Actualisation…" : `${items.length} publications`}
              </span>
            </div>
          </div>
        </div>

        {/* Bandeau défilant */}
        <div className="relative overflow-hidden h-8 border-t border-white/10">
          <div className="absolute inset-y-0 left-0 flex items-center whitespace-nowrap animate-marquee">
            {[...items, ...items].slice(0, 12).map((n, i) => (
              <span key={i} className="text-xs text-white/55 px-6 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gold inline-block" />
                {n.title}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section className="bg-white border-b border-border sticky top-0 z-30" data-testid="news-filters">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une actualité…"
                   className="w-full border border-border rounded-sm pl-10 pr-3 py-2 text-sm focus:border-ink outline-none"
                   data-testid="news-search" />
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none py-1 -mx-2 px-2 flex-1">
            <FilterChip active={!activeCat} onClick={() => setActiveCat("")}>Tout</FilterChip>
            {cats.map((c) => (
              <FilterChip key={c.id} active={activeCat === c.name} onClick={() => setActiveCat(c.name)}>
                {c.name}
              </FilterChip>
            ))}
          </div>
        </div>
      </section>

      {/* Liste articles */}
      <section className="py-16 lg:py-20 bg-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <NewsSkeleton />
          ) : error ? (
            <ErrorState onRetry={fetchNews} />
          ) : filtered.length === 0 ? (
            <div className="text-foreground/60 py-16 text-center" data-testid="news-empty">
              <Newspaper className="w-10 h-10 text-foreground/25 mx-auto mb-3" />
              Aucune actualité{activeCat && ` dans la catégorie « ${activeCat} »`}.
            </div>
          ) : (
            <div className="space-y-10">
              {filtered.map((n, i) => (
                <NewsRow key={n.id} n={n} large={i === 0 && !activeCat && !q} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function NewsSkeleton() {
  return (
    <div className="space-y-10" data-testid="news-skeleton">
      {[0, 1, 2].map((i) => (
        <div key={i} className="grid md:grid-cols-12 gap-6 lg:gap-8 items-start border-b border-border pb-10 animate-pulse">
          <div className="md:col-span-5 aspect-[16/10] rounded-sm bg-ink/10" />
          <div className="md:col-span-7 space-y-4">
            <div className="h-3 w-32 bg-ink/10 rounded" />
            <div className="h-7 w-4/5 bg-ink/15 rounded" />
            <div className="h-4 w-full bg-ink/10 rounded" />
            <div className="h-4 w-2/3 bg-ink/10 rounded" />
          </div>
        </div>
      ))}
      <div className="text-center text-foreground/40 text-sm flex items-center justify-center gap-2 pt-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement des actualités…
      </div>
    </div>
  );
}

function ErrorState({ onRetry }) {
  return (
    <div className="max-w-xl mx-auto text-center py-16" data-testid="news-error">
      <div className="w-14 h-14 mx-auto rounded-full bg-red-50 border border-red-100 grid place-items-center text-red-600 mb-5">
        <AlertTriangle className="w-6 h-6" />
      </div>
      <h2 className="font-serif text-2xl lg:text-3xl text-ink">
        Nos actualités sont temporairement indisponibles
      </h2>
      <p className="text-foreground/65 mt-3 leading-relaxed text-sm">
        Nous n'avons pas pu joindre le serveur pour récupérer les publications.
        Cela peut être un incident réseau passager. Réessayez dans quelques secondes.
      </p>
      <button
        onClick={onRetry}
        className="mt-6 inline-flex items-center gap-2 bg-ink text-white px-5 py-2.5 rounded-sm text-sm font-semibold hover:bg-ink/90 transition-colors"
        data-testid="news-retry"
      >
        <RefreshCw className="w-4 h-4" /> Réessayer
      </button>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              active ? "bg-ink text-white" : "bg-white border border-border text-foreground/70 hover:border-ink"
            }`}
            data-testid={`news-cat-${(children || "").toString().toLowerCase().replace(/\s+/g, "-")}`}>
      {children}
    </button>
  );
}

function NewsRow({ n, large }) {
  return (
    <Link to={`/actualites/${n.slug}`} data-testid={`news-${n.slug}`}
          className={`group grid md:grid-cols-12 gap-6 lg:gap-8 items-start border-b border-border pb-10 ${large ? "lg:pb-16" : ""}`}>
      <div className={`md:col-span-5 overflow-hidden rounded-sm bg-ink/5 ${large ? "aspect-[4/3]" : "aspect-[16/10]"}`}>
        {n.cover_image
          ? <img src={n.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          : <div className="w-full h-full grid place-items-center text-foreground/30"><Newspaper className="w-10 h-10" /></div>
        }
      </div>
      <div className="md:col-span-7">
        <div className="flex items-center gap-2 flex-wrap">
          {n.is_pinned && (
            <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider bg-ink text-gold px-2 py-0.5 rounded"><Pin className="w-2.5 h-2.5" /> Épinglé</span>
          )}
          {n.is_featured && (
            <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider bg-gold text-ink px-2 py-0.5 rounded"><Star className="w-2.5 h-2.5" /> À la une</span>
          )}
          <div className="eyebrow text-gold-dark">{n.category} {n.location && `· ${n.location}`} · {fmt(n.published_at)}</div>
        </div>
        <h2 className={`font-serif text-ink mt-3 leading-tight ${large ? "text-3xl lg:text-5xl" : "text-2xl lg:text-3xl"}`}>
          {n.title}
        </h2>
        <p className="mt-4 text-foreground/70 leading-relaxed">{n.excerpt}</p>
        <div className="mt-5 flex items-center gap-5 text-sm">
          <span className="inline-flex items-center gap-1.5 text-ink font-semibold group-hover:gap-3 transition-all">
            Lire l'article <ArrowRight className="w-4 h-4" />
          </span>
          <span className="inline-flex items-center gap-1 text-foreground/45 text-xs">
            <Eye className="w-3.5 h-3.5" /> {n.view_count || 0} vues
          </span>
        </div>
      </div>
    </Link>
  );
}
